/**
 * Shared types and wall-port helpers used by every level file. Lives in its own
 * module to avoid circular imports between `index.ts` (which aggregates levels)
 * and per-level files (which need these helpers).
 */

import { WORKSPACE } from '../grid';
import type { Direction, MoleculeMix } from '../types';
import type { OrganDef } from '../entities/Organ';

export interface WallPortConfig {
  id: string;
  kind: 'spout' | 'receptacle' | 'bidirectional';
  facing: Direction;
  /** Connection cell — where the pipe attaches (inside the workspace). */
  connectionCell: { col: number; row: number };
  carries: MoleculeMix;
  /**
   * Receptacles only. Additional source mixes that count as CORRECT in addition
   * to `carries`. See `PortConfig.acceptsAlso` for full semantics.
   */
  acceptsAlso?: MoleculeMix[];
  /**
   * Spouts only (usually). If true, render the porthole base but skip the
   * molecule icons inside it — pedagogically a "discovery" spout. See
   * `PortConfig.hideContents`.
   */
  hideContents?: boolean;
  /**
   * Output spouts only. Wall-port equivalent of `OrganPortDef.dependsOn` —
   * port IDs (typically other wall receptacles) that must currently be
   * receiving their declared `carries` for this wall spout to emit. Per-frame
   * `Connector.update` evaluates the rule the same way it does for organ
   * spouts. Used by Level 6 to gate one wall spout on the wall receptacle
   * above it ("body sends O₂ back out only once O₂-rich blood arrives").
   */
  dependsOn?: string[];
  /**
   * Optional decorative fixture texture rendered behind the cone (e.g. the
   * toilet). See `PortConfig.decoration`. Currently only used by the bottom
   * waste receptacle via the `toiletReceptacle` helper.
   */
  decoration?: string;
}

export interface LevelConfig {
  step: number;
  title: string; // "STEP ONE"
  instructions: string; // clipboard body text
  ports: WallPortConfig[];
  organPool?: OrganDef[];
  /**
   * Optional. If set, the organ that owns this port id will visually activate
   * (texture swap + pulse) when food first arrives at it during START FLUIDS.
   * Used in Step 3 for the brain.
   */
  activationPortId?: string;
  /**
   * Optional structural prerequisites for "solved", checked ON TOP of the
   * usual correct-spout count. Each entry names two port ids that must be
   * joined by a flowing pipe (either drag orientation) for the level to
   * complete. Closes degenerate-solve loopholes the count alone can't catch.
   *
   * L5/L8 use this to REQUIRE the trachea (atmosphere ↔ lungs top). The
   * atmosphere port is bidirectional (an O₂ source), so without this a student
   * could pipe the atmosphere straight into the O₂ wall receptacle — padding
   * the correct-pipe count to the spout total while completely bypassing the
   * lungs. Requiring the trachea also makes that cheat physically impossible:
   * the atmosphere port can feed the trachea OR the wall, not both.
   */
  requiredLinks?: Array<{ a: string; b: string }>;
}

export const MID_ROW = Math.floor(WORKSPACE.rows / 2);
export const LAST_COL = WORKSPACE.cols - 1;
export const LAST_ROW = WORKSPACE.rows - 1;

export const leftSpout = (id: string, row: number, carries: MoleculeMix): WallPortConfig => ({
  id,
  kind: 'spout',
  facing: 'E',
  connectionCell: { col: 0, row },
  carries,
});
export const leftReceptacle = (id: string, row: number, carries: MoleculeMix): WallPortConfig => ({
  id,
  kind: 'receptacle',
  facing: 'E',
  connectionCell: { col: 0, row },
  carries,
});
export const rightReceptacle = (id: string, row: number, carries: MoleculeMix): WallPortConfig => ({
  id,
  kind: 'receptacle',
  facing: 'W',
  connectionCell: { col: LAST_COL, row },
  carries,
});
export const rightSpout = (id: string, row: number, carries: MoleculeMix): WallPortConfig => ({
  id,
  kind: 'spout',
  facing: 'W',
  connectionCell: { col: LAST_COL, row },
  carries,
});
export const topSpout = (id: string, col: number, carries: MoleculeMix): WallPortConfig => ({
  id,
  kind: 'spout',
  facing: 'S',
  connectionCell: { col, row: 0 },
  carries,
});
export const bottomReceptacle = (
  id: string,
  col: number,
  carries: MoleculeMix,
): WallPortConfig => ({
  id,
  kind: 'receptacle',
  facing: 'N',
  connectionCell: { col, row: LAST_ROW },
  carries,
});

/**
 * Bottom-wall waste receptacle drawn as a toilet. Same as `bottomReceptacle`
 * with `carries: ['waste']`, plus the `fixture-toilet` decoration so the toilet
 * bowl renders behind the inlet cone. Used by every `*-toilet` port (L4/6/10/11).
 */
export const toiletReceptacle = (id: string, col: number): WallPortConfig => ({
  ...bottomReceptacle(id, col, ['waste']),
  decoration: 'fixture-toilet',
});

/**
 * Ceiling-mounted bidirectional "atmosphere" port. Used by the trachea —
 * spouts O₂ down and accepts CO₂ up. The pipe between this and a lungs'
 * top port automatically becomes a `specialKind: 'trachea'` pipe (gray
 * segments, single-sprite alternating flow).
 */
export const topAtmosphere = (
  id: string,
  col: number,
  carries: MoleculeMix,
): WallPortConfig => ({
  id,
  kind: 'bidirectional',
  facing: 'S',
  connectionCell: { col, row: 0 },
  carries,
});

/**
 * UI keep-out columns for wall ports, due to chrome that lives in front of the
 * port porthole position. See `AGENT_PROMPT.md` for the diagram.
 */
export const SAFE_TOP_COLS = { min: 5, max: 17 } as const;
export const SAFE_BOTTOM_COL_RANGES: Array<{ min: number; max: number }> = [
  { min: 0, max: 7 },
  { min: 17, max: 19 },
];

/**
 * Lightweight runtime sanity check, called by `GameScene.loadLevel` after a
 * level config is loaded. Warns (doesn't throw) on:
 *   • Two ports sharing a `connectionCell` (a placement bug — the second port
 *     would render on top of the first and behave unpredictably).
 *   • A top/bottom-wall port placed in a UI keep-out column (porthole hides
 *     behind the top bar or clipboard).
 *   • Duplicate port IDs (would break port lookups).
 *
 * Output goes to `console.warn` only — never blocks the level from loading,
 * since the game is still playable even with overlapping ports (just buggy).
 */
export function validateLevel(cfg: LevelConfig): string[] {
  const warnings: string[] = [];
  const seenIds = new Set<string>();
  const cellKey = (c: { col: number; row: number }) => `${c.col},${c.row}`;
  const cellOwner = new Map<string, string>(); // cell key → first port id at that cell

  for (const p of cfg.ports) {
    if (seenIds.has(p.id)) {
      warnings.push(`Level ${cfg.step}: duplicate port id "${p.id}"`);
    }
    seenIds.add(p.id);

    const key = cellKey(p.connectionCell);
    if (cellOwner.has(key)) {
      warnings.push(
        `Level ${cfg.step}: ports "${cellOwner.get(key)}" and "${p.id}" both occupy cell (${p.connectionCell.col}, ${p.connectionCell.row})`,
      );
    } else {
      cellOwner.set(key, p.id);
    }

    // UI keep-out: top wall (row 0) hides behind the top bar at cols 0–4 and 18–19.
    if (p.connectionCell.row === 0) {
      const c = p.connectionCell.col;
      if (c < SAFE_TOP_COLS.min || c > SAFE_TOP_COLS.max) {
        warnings.push(
          `Level ${cfg.step}: top-wall port "${p.id}" at col ${c} hides behind the top bar (use cols ${SAFE_TOP_COLS.min}–${SAFE_TOP_COLS.max})`,
        );
      }
    }
    // UI keep-out: bottom wall (last row) hides behind the clipboard at cols 8–16.
    if (p.connectionCell.row === LAST_ROW) {
      const c = p.connectionCell.col;
      const inSafe = SAFE_BOTTOM_COL_RANGES.some((r) => c >= r.min && c <= r.max);
      if (!inSafe) {
        warnings.push(
          `Level ${cfg.step}: bottom-wall port "${p.id}" at col ${c} hides behind the clipboard (use cols 0–7 or 17–19)`,
        );
      }
    }
  }

  return warnings;
}
